import { Box, Collapse, makeStyles } from '@material-ui/core';

import { Font } from '@/components/shared/components/Font';
import { FormGridContainer } from '@/components/shared/components/FormGridContainer/FormGridContainer';
import { AutocompleteNationalityFormField } from '@/components/shared/components/NewContextForms/AutocompleteNationalityFormField/AutocompleteNationalityFormField';
import { AutocompleteAddressHeadless } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/AutocompleteAddressHeadless';
import { useAddressContext } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/contexts/AddressContext';
import { AddressMode } from '@/components/shared/components/NewContextForms/components/AutocompleteAddressHeadless/types/AddressMode';
import { CustomTextField } from '@/components/shared/components/NewContextForms/shared/CustomTextField';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { Text } from '@/components/shared/components/Text';
import { ENV_MAPS_API_KEY } from '@/config/envVars';
import { useScreen } from '@/hooks/useScreen';

interface AutocompleteAddressProps {
  fieldVariant: FieldVariant | undefined;
  fieldNamesPrefix?: string;
  hideAdditionalFields?: boolean;
  onSaveForm?: () => void;
}

export const AutocompleteAddress = ({
  fieldVariant,
  fieldNamesPrefix,
  hideAdditionalFields = false,
  onSaveForm,
}: AutocompleteAddressProps) => {
  return (
    <AutocompleteAddressHeadless fieldNamesPrefix={fieldNamesPrefix}>
      <AutocompleteAddressHeadless.AddressPreview />
      <AutocompleteAddressHeadless.FloorNumber as={<CustomTextField markAsOptional />} />
      <AutocompleteAddressHeadless.DoorNumber as={<CustomTextField markAsOptional />} />
      <AutocompleteAddressHeadless.Building as={<CustomTextField markAsOptional />} />
      <AutocompleteAddressDialog
        fieldVariant={fieldVariant}
        hideAdditionalFields={hideAdditionalFields}
        onSaveForm={onSaveForm}
      />
    </AutocompleteAddressHeadless>
  );
};

interface AutocompleteAddressDialogProps {
  fieldVariant: FieldVariant | undefined;
  hideAdditionalFields: boolean;
  onSaveForm?: () => void;
}

const AutocompleteAddressDialog = ({
  fieldVariant,
  hideAdditionalFields,
  onSaveForm,
}: AutocompleteAddressDialogProps) => {
  const { mode } = useAddressContext();
  const { sm } = useScreen();

  const collapseStyles = {
    root: {
      transition: 'all 0.5s ease',
      transform: 'none!important',
      '&:not(.MuiCollapse-entered)': {
        transition: 'all 0.2s ease',
        opacity: 0,
        transform: 'none!important',
      },
    },
  };
  const getCollapseStyles = makeStyles(collapseStyles);
  const collapseClasses = getCollapseStyles();

  return (
    <AutocompleteAddressHeadless.Dialog
      onSaveForm={onSaveForm}
      {...(hideAdditionalFields ? { titleTextId: 'AUTOCOMPLETE_ADDRESS' } : { subtitleTextId: 'AUTOCOMPLETE_ADDRESS' })}
    >
      <Collapse in={mode === AddressMode.CONFIRM} classes={collapseClasses}>
        <Box pb={2}>
          <AutocompleteAddressHeadless.AddressCard />
        </Box>
      </Collapse>
      <Collapse in={mode === AddressMode.AUTOCOMPLETE} classes={collapseClasses}>
        <Box mb={2}>
          <Font variant="body2" color="textSecondary">
            <Text tId={'AUTOCOMPLETE_HINT'} />
          </Font>
        </Box>
        <Box pb={2}>
          <AutocompleteAddressHeadless.Address apiKey={ENV_MAPS_API_KEY} />
          <Box display={'flex'} justifyContent={'flex-end'} mt={2}>
            <AutocompleteAddressHeadless.CancelChangesButton />
          </Box>
        </Box>
      </Collapse>
      <Collapse in={mode === AddressMode.MANUAL || mode === AddressMode.EDIT} classes={collapseClasses}>
        <FormGridContainer fieldVariant={fieldVariant} fullWidth={false} direction={'row'} customBreakpoint={!sm}>
          <AutocompleteAddressHeadless.StreetName as={<CustomTextField />} />
          <AutocompleteAddressHeadless.StreetNumber as={<CustomTextField />} />
          <AutocompleteAddressHeadless.PostalCode as={<CustomTextField />} />
          <AutocompleteAddressHeadless.City as={<CustomTextField />} />
          <AutocompleteAddressHeadless.Province as={<CustomTextField />} />
          {/* @ts-expect-error Property 'name' is missing in type '{ useAlpha2: true; }' but required in type 'AutocompleteNationalityFormFieldProps<FieldValues, unknown>'. */}
          <AutocompleteAddressHeadless.Country as={<AutocompleteNationalityFormField useAlpha2 />} />
        </FormGridContainer>
        <Box display={'flex'} justifyContent={'flex-end'} gridGap={'8px'} mt={3}>
          <AutocompleteAddressHeadless.CancelChangesButton />
          <AutocompleteAddressHeadless.ConfirmChangesButton />
        </Box>
      </Collapse>
      {!hideAdditionalFields && (
        <>
          <Box pt={5} pb={3}>
            <Font variant="h5">
              <Text tId={'ADDITIONAL_DETAILS'} />
            </Font>
          </Box>
          <FormGridContainer fieldVariant={fieldVariant} fullWidth={false} direction={'row'} customBreakpoint={!sm}>
            <AutocompleteAddressHeadless.FloorNumber as={<CustomTextField markAsOptional />} />
            <AutocompleteAddressHeadless.DoorNumber as={<CustomTextField markAsOptional />} />
            <AutocompleteAddressHeadless.Building as={<CustomTextField markAsOptional />} />
          </FormGridContainer>
        </>
      )}
    </AutocompleteAddressHeadless.Dialog>
  );
};
